import React, { useState, useEffect } from 'react';
import { Modal, Card, Typography, Space, Button, Row, Col, InputNumber, Radio, Input, Divider, Alert, App, Popconfirm } from 'antd';
import {
  PercentageOutlined,
  DollarOutlined,
  TagOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  CalculatorOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { Table } from '../../../types/tables';
import { Order, OrderItem } from '../../../types/orders';
import ItemDiscountModal from './ItemDiscountModal';
import { discountLabel, isItemDiscount, itemDiscountTag, itemDiscountsById, netItemTotal } from '../../../utils/discounts';
import { useResponsive } from '../../../hooks/useResponsive';
import { useGetRestaurantSettingsQuery } from '../../../services/restaurantsApi';
import { useGetOrderDiscountsQuery, useRemoveDiscountMutation } from '../../../services/ordersApi';
import { getErrorMessage } from '../../../utils/errorHelpers';

const { Title, Text } = Typography;
const { TextArea } = Input;

export interface DiscountData {
  type: 'percentage' | 'amount';
  value: number;
  reason: string;
  notes?: string;
  authorizedBy: string;
}

interface DiscountModalProps {
  visible: boolean;
  onCancel: () => void;
  table?: Table;
  order?: Order;
  onApplyDiscount?: (discount: DiscountData) => Promise<void>;
  onDiscountRemoved?: () => void;
  /** Se aplicó una cortesía/descuento a un producto desde este modal. */
  onItemDiscountApplied?: () => void;
}

const DiscountModal: React.FC<DiscountModalProps> = ({
  visible,
  onCancel,
  table,
  order,
  onApplyDiscount,
  onDiscountRemoved,
  onItemDiscountApplied,
}) => {
  const { message } = App.useApp();
  const [discountType, setDiscountType] = useState<'percentage' | 'amount'>('percentage');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [authorizedBy, setAuthorizedBy] = useState<string>('');
  const [loading, setLoading] = useState(false);
  // Producto al que se le aplica cortesía desde aquí (para llevar y domicilio
  // no tienen el menú de mesa, este es su único camino)
  const [itemTarget, setItemTarget] = useState<OrderItem | null>(null);
  const { isMobile, isTablet } = useResponsive();

  // Get restaurant settings for tax rate
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery();
  const taxRate = (restaurantSettings?.taxRate || 16) / 100; // Convert percentage to decimal

  // Discounts already applied to the open order (backend returns active only)
  const { data: appliedDiscounts = [] } = useGetOrderDiscountsQuery(order?.id ?? '', {
    skip: !visible || !order,
  });
  const [removeDiscount, { isLoading: removingDiscount }] = useRemoveDiscountMutation();

  // Limpiar el formulario cada vez que se abre. `destroyOnClose` desmonta el CONTENIDO del
  // Modal pero no este componente, así que los useState sobreviven al cierre: desde la
  // lista de órdenes, donde el modal se monta una sola vez para todas las filas, los
  // valores de la orden anterior reaparecían precargados y ya válidos en la siguiente —un
  // clic de distancia de registrar un descuento que nadie eligió. Mismo patrón que
  // PaymentModal. Los setters se repiten en vez de llamar a handleReset porque ese vive
  // después del early return y no existe cuando no hay orden.
  useEffect(() => {
    if (visible) {
      setDiscountType('percentage');
      setDiscountValue(0);
      setSelectedReason('');
      setCustomReason('');
      setNotes('');
      setAuthorizedBy('');
    }
  }, [visible]);

  if (!order) return null;

  // El prop `table` solo llega desde el menú de mesas. Desde la lista de órdenes no hay
  // objeto Table a mano —y las órdenes para llevar/domicilio ni siquiera tienen mesa
  // real—, así que la cuenta se identifica con los datos de la orden. Mismo criterio que
  // PaymentModal, que ya se abre desde las dos pantallas.
  const orderTableLabel = order.type === 'DineIn'
    ? (order.tableName ? `Mesa ${order.tableName}` : 'Mesa')
    : order.type === 'Delivery' ? 'Domicilio' : 'Para Llevar';
  const displayLabel = table ? `Mesa ${table.number}` : orderTableLabel;

  const handleRemoveDiscount = async (discountId: string) => {
    try {
      await removeDiscount(discountId).unwrap();
      message.success('Descuento eliminado');
      onDiscountRemoved?.();
    } catch (error) {
      console.error('Error removing discount:', error);
      message.error('Error al quitar el descuento');
    }
  };

  // Predefined discount reasons
  const discountReasons = [
    { id: 'customer_complaint', label: 'Queja del Cliente', maxPercentage: 50 },
    { id: 'service_delay', label: 'Demora en el Servicio', maxPercentage: 25 },
    { id: 'food_quality', label: 'Calidad de la Comida', maxPercentage: 100 },
    { id: 'birthday_promotion', label: 'Promoción Cumpleaños', maxPercentage: 20 },
    { id: 'loyalty_customer', label: 'Cliente Frecuente', maxPercentage: 15 },
    { id: 'staff_error', label: 'Error del Personal', maxPercentage: 100 },
    { id: 'manager_approval', label: 'Aprobación Gerencial', maxPercentage: 100 },
    { id: 'custom', label: 'Otro (especificar)', maxPercentage: 100 },
  ];

  // Quick discount percentages
  const quickPercentages = [5, 10, 15, 20, 25, 50];

  // Calculate totals
  const orderItems = order.orderItems || [];
  const activeItems = orderItems.filter(item => !item.isCancelled);

  // NOTE: Total price is used as base for tax calculation
  // Tax is calculated as a percentage of the total, then subtracted to get subtotal
  const totalWithTax = activeItems.reduce((sum, item) => sum + item.total, 0);
  const tax = totalWithTax * taxRate;
  const subtotal = totalWithTax - tax;
  // El descuento a la cuenta se calcula sobre lo que queda después de las
  // cortesías y descuentos de producto (así lo hace el backend).
  const itemDiscounts = itemDiscountsById(appliedDiscounts);
  const itemDiscountTotal = appliedDiscounts
    .filter(isItemDiscount)
    .reduce((sum: number, d: { discountAmount: number }) => sum + Number(d.discountAmount), 0);
  const totalBeforeDiscount = totalWithTax - itemDiscountTotal;
  
  const discountAmount = discountType === 'percentage' 
    ? totalBeforeDiscount * (discountValue / 100)
    : discountValue;
  
  const totalAfterDiscount = Math.max(0, totalBeforeDiscount - discountAmount);
  const discountPercentageApplied = totalBeforeDiscount > 0 
    ? (discountAmount / totalBeforeDiscount) * 100 
    : 0;

  // Validation
  const selectedReasonData = discountReasons.find(r => r.id === selectedReason);
  const maxAllowedPercentage = selectedReasonData?.maxPercentage || 0;
  const isDiscountValid = discountValue > 0 && discountPercentageApplied <= maxAllowedPercentage;
  const isFormValid = isDiscountValid && selectedReason && authorizedBy.trim();

  const handleQuickPercentage = (percentage: number) => {
    setDiscountType('percentage');
    setDiscountValue(percentage);
  };

  const handleMaxDiscount = () => {
    if (selectedReasonData) {
      setDiscountType('percentage');
      setDiscountValue(selectedReasonData.maxPercentage);
    }
  };

  const handleApplyDiscount = async () => {
    if (!isFormValid) {
      message.error('Por favor complete todos los campos requeridos');
      return;
    }

    const finalReason = selectedReason === 'custom' ? customReason : selectedReasonData?.label || '';
    if (selectedReason === 'custom' && !customReason.trim()) {
      message.error('Debe especificar el motivo personalizado');
      return;
    }

    setLoading(true);
    try {
      const discountData: DiscountData = {
        type: discountType,
        value: discountValue,
        reason: finalReason,
        notes: notes.trim() || undefined,
        authorizedBy: authorizedBy.trim(),
      };

      await onApplyDiscount?.(discountData);
      message.success('Descuento aplicado exitosamente');
      onCancel();
    } catch (error) {
      // El mensaje del backend es el que sirve: los rechazos reales son explicables
      // ("la orden tiene la cuenta dividida", 403 por rol) y un genérico los escondía.
      message.error(getErrorMessage(error, 'Error al aplicar descuento'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDiscountType('percentage');
    setDiscountValue(0);
    setSelectedReason('');
    setCustomReason('');
    setNotes('');
    setAuthorizedBy('');
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={isMobile ? '100vw' : isTablet ? '90vw' : 800}
      style={{ 
        top: isMobile ? 0 : 20,
        maxWidth: isMobile ? '100vw' : '800px',
        height: isMobile ? '100vh' : 'auto'
      }}
      styles={{ 
        body: { 
          padding: 0,
          height: isMobile ? '100vh' : 'auto',
          overflowY: isMobile ? 'auto' : 'visible'
        } 
      }}
      destroyOnClose
      centered={!isMobile}
    >
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        {/* Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: isMobile ? '16px' : '24px',
          borderBottom: '2px solid #fa8c16',
          paddingBottom: isMobile ? '12px' : '16px'
        }}>
          <Title level={isMobile ? 3 : 2} style={{ marginBottom: '8px', color: '#fa8c16' }}>
            <PercentageOutlined style={{ marginRight: '8px' }} />
            {isMobile ? 'DESCUENTO' : 'APLICAR DESCUENTO'}
          </Title>
          <Text strong style={{ fontSize: '16px' }}>
            {displayLabel} - Orden #{order.orderNumber}
          </Text>
        </div>

        <Row gutter={isMobile ? [0, 16] : [24, 0]}>
          {/* Left Column - Discount Configuration */}
          <Col xs={24} lg={14}>
            {/* Discount Reason */}
            <Card 
              title={
                <Space>
                  <ExclamationCircleOutlined />
                  <span>{isMobile ? 'Motivo' : 'Motivo del Descuento'}</span>
                </Space>
              }
              size="small" 
              style={{ marginBottom: isMobile ? '12px' : '16px' }}
            >
              <div style={{ marginBottom: '12px' }}>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                  Seleccione el motivo: *
                </Text>
                <Radio.Group 
                  value={selectedReason} 
                  onChange={(e) => setSelectedReason(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {discountReasons.map(reason => (
                      <Radio key={reason.id} value={reason.id}>
                        <Space>
                          <span>{reason.label}</span>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            (máx. {reason.maxPercentage}%)
                          </Text>
                        </Space>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>

              {/* Custom Reason Input */}
              {selectedReason === 'custom' && (
                <div style={{ marginBottom: '12px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Especificar motivo: *
                  </Text>
                  <Input
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Descripción del motivo del descuento..."
                    maxLength={100}
                  />
                </div>
              )}
            </Card>

            {/* Discount Type */}
            <Card 
              title={
                <Space>
                  <TagOutlined />
                  <span>{isMobile ? 'Tipo' : 'Tipo de Descuento'}</span>
                </Space>
              }
              size="small" 
              style={{ marginBottom: isMobile ? '12px' : '16px' }}
            >
              <Radio.Group 
                value={discountType} 
                onChange={(e) => setDiscountType(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Radio value="percentage">
                    <Space>
                      <PercentageOutlined />
                      <span>Porcentaje (%)</span>
                    </Space>
                  </Radio>
                  <Radio value="amount">
                    <Space>
                      <DollarOutlined />
                      <span>Monto Fijo ($)</span>
                    </Space>
                  </Radio>
                </Space>
              </Radio.Group>
            </Card>

            {/* Discount Value */}
            <Card 
              title={
                <Space>
                  <CalculatorOutlined />
                  <span>{isMobile ? 'Valor' : 'Valor del Descuento'}</span>
                </Space>
              }
              size="small" 
              style={{ marginBottom: isMobile ? '12px' : '16px' }}
            >
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                  {discountType === 'percentage' ? 'Porcentaje de descuento:' : 'Monto de descuento:'}
                </Text>
                <InputNumber
                  value={discountValue}
                  onChange={(value) => setDiscountValue(value || 0)}
                  onFocus={(e) => e.target.select()}
                  style={{ width: '100%' }}
                  size={isMobile ? "middle" : "large"}
                  min={0}
                  max={discountType === 'percentage' ? maxAllowedPercentage : totalBeforeDiscount}
                  precision={discountType === 'percentage' ? 0 : 2}
                  prefix={discountType === 'percentage' ? '%' : '$'}
                />
              </div>

              {/* Quick Percentage Buttons */}
              {discountType === 'percentage' && (
                <div style={{ marginBottom: '12px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Descuentos rápidos:
                  </Text>
                  <Space wrap size={[8, 16]}>
                    {quickPercentages.map(percentage => (
                      <Button
                        key={percentage}
                        size={"small"}
                        onClick={() => handleQuickPercentage(percentage)}
                        disabled={percentage > maxAllowedPercentage}
                        type={discountValue === percentage ? 'primary' : 'default'}
                        style={{ width: '48px', textAlign: 'center' }}
                      >
                        {percentage}%
                      </Button>
                    ))}
                  </Space>
                </div>
              )}

              {/* Max Discount Button */}
              {selectedReasonData && maxAllowedPercentage > 0 && (
                <Button
                  onClick={handleMaxDiscount}
                  style={{ marginBottom: '12px' }}
                  disabled={discountValue === maxAllowedPercentage}
                >
                  Máximo Permitido ({maxAllowedPercentage}%)
                </Button>
              )}

              {/* Validation Messages */}
              {discountValue > 0 && discountPercentageApplied > maxAllowedPercentage && (
                <Alert
                  message="Descuento Excesivo"
                  description={`El descuento supera el máximo permitido (${maxAllowedPercentage}%) para este motivo`}
                  type="error"
                  showIcon
                  style={{ marginTop: '8px' }}
                />
              )}
            </Card>

            {/* Additional Notes */}
            <Card 
              title={
                <Space>
                  <ExclamationCircleOutlined />
                  <span>{isMobile ? 'Notas' : 'Notas Adicionales'}</span>
                </Space>
              }
              size="small" 
              style={{ marginBottom: isMobile ? '12px' : '16px' }}
            >
              <div>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                  Notas adicionales (opcional):
                </Text>
                <TextArea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Información adicional sobre el descuento..."
                  rows={2}
                  maxLength={200}
                  showCount
                />
              </div>
            </Card>

            {/* Authorization */}
            <Card 
              title={
                <Space>
                  <UserOutlined />
                  <span>Autorización</span>
                </Space>
              }
              size="small"
            >
              <div>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                  Autorizado por: *
                </Text>
                <Input
                  value={authorizedBy}
                  onChange={(e) => setAuthorizedBy(e.target.value)}
                  placeholder="Nombre del supervisor/gerente que autoriza"
                  maxLength={50}
                  size={isMobile ? "middle" : "large"}
                />
              </div>
            </Card>
          </Col>

          {/* Right Column - Summary */}
          <Col xs={24} lg={10}>
            {/* Cortesía o descuento a UN producto. Se quita desde "Descuentos Aplicados". */}
            {activeItems.length > 0 && (
              <Card
                title={
                  <Space>
                    <GiftOutlined />
                    <span>Por producto</span>
                  </Space>
                }
                size="small"
                style={{ marginBottom: isMobile ? '12px' : '16px' }}
              >
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {activeItems.map(item => {
                    const itemDiscount = itemDiscounts[item.id];
                    return (
                      <Row key={item.id} justify="space-between" align="middle" wrap={false} gutter={8}>
                        <Col flex="auto" style={{ minWidth: 0 }}>
                          <Text ellipsis style={{ display: 'block' }}>
                            {item.quantity}x {item.dailyMenuName || item.productName}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>
                            ${netItemTotal(item, itemDiscounts).toFixed(2)}
                            {itemDiscount ? ` · ${itemDiscountTag(itemDiscount, item)}` : ''}
                          </Text>
                        </Col>
                        <Col>
                          {itemDiscount ? (
                            <Text type="secondary" style={{ fontSize: '12px' }}>Aplicado</Text>
                          ) : (
                            <Button size="small" onClick={() => setItemTarget(item)}>
                              Descontar
                            </Button>
                          )}
                        </Col>
                      </Row>
                    );
                  })}
                </Space>
              </Card>
            )}
            {/* Descuentos ya aplicados a la orden — quitar = Popconfirm; "modificar" = quitar + aplicar de nuevo */}
            {appliedDiscounts.length > 0 && (
              <Card
                title={
                  <Space>
                    <TagOutlined />
                    <span>Descuentos Aplicados</span>
                  </Space>
                }
                size="small"
                style={{ marginBottom: isMobile ? '12px' : '16px' }}
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {appliedDiscounts.map((d: any) => (
                    <Row key={d.id} justify="space-between" align="middle" wrap={false}>
                      <Col flex="auto">
                        <div>
                          <Text strong>{discountLabel(d, order.orderItems)}</Text>
                        </div>
                        <div>
                          <Text style={{ color: '#fa8c16' }}>
                            -${Number(d.discountAmount).toFixed(2)} ({Number(d.discountPercentage).toFixed(1)}%)
                          </Text>
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Autorizó: {d.authorizedBy}
                          </Text>
                        </div>
                      </Col>
                      <Col>
                        <Popconfirm
                          title="¿Quitar este descuento?"
                          description="La orden volverá a su total sin este descuento."
                          okText="Quitar"
                          okType="danger"
                          cancelText="Cancelar"
                          onConfirm={() => handleRemoveDiscount(d.id)}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            loading={removingDiscount}
                          />
                        </Popconfirm>
                      </Col>
                    </Row>
                  ))}
                </Space>
              </Card>
            )}
            <Card
              title={
                <Space>
                  <CalculatorOutlined />
                  <span>{isMobile ? 'Resumen' : 'Resumen de Descuento'}</span>
                </Space>
              }
              size="small"
              style={{ marginBottom: isMobile ? '12px' : '16px' }}
            >
              <div style={{ marginBottom: '16px' }}>
                <Row justify="space-between" style={{ marginBottom: '8px' }}>
                  <Text>Productos ({activeItems.length}):</Text>
                  <Text>${subtotal.toFixed(2)}</Text>
                </Row>
                <Row justify="space-between" style={{ marginBottom: '8px' }}>
                  <Text>IVA ({(taxRate * 100).toFixed(0)}%):</Text>
                  <Text>${tax.toFixed(2)}</Text>
                </Row>
                {itemDiscountTotal > 0 && (
                  <Row justify="space-between" style={{ marginBottom: '8px' }}>
                    <Text style={{ color: '#fa8c16' }}>Descuentos por producto:</Text>
                    <Text style={{ color: '#fa8c16' }}>-${itemDiscountTotal.toFixed(2)}</Text>
                  </Row>
                )}
                <Row justify="space-between" style={{ marginBottom: '8px' }}>
                  <Text strong>{itemDiscountTotal > 0 ? 'Total a descontar:' : 'Total Original:'}</Text>
                  <Text strong>${totalBeforeDiscount.toFixed(2)}</Text>
                </Row>
                
                <Divider style={{ margin: '12px 0' }} />
                
                <Row justify="space-between" style={{ marginBottom: '8px' }}>
                  <Text style={{ color: '#fa8c16' }}>
                    Descuento ({discountPercentageApplied.toFixed(1)}%):
                  </Text>
                  <Text style={{ color: '#fa8c16' }}>-${discountAmount.toFixed(2)}</Text>
                </Row>
                
                <Divider style={{ margin: '12px 0' }} />
                
                <Row justify="space-between">
                  <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                    Total Final:
                  </Text>
                  <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                    ${totalAfterDiscount.toFixed(2)}
                  </Text>
                </Row>
              </div>

              {/* Savings Display */}
              {discountAmount > 0 && (
                <Alert
                  message={`Ahorro: $${discountAmount.toFixed(2)}`}
                  description={`El cliente ahorra ${discountPercentageApplied.toFixed(1)}% del total`}
                  type="success"
                  showIcon
                  style={{ marginBottom: isMobile ? '12px' : '16px' }}
                />
              )}

              {/* Validation Status */}
              {!isFormValid && discountValue > 0 && (
                <Alert
                  message="Información Incompleta"
                  description="Complete todos los campos requeridos antes de aplicar el descuento"
                  type="warning"
                  showIcon
                  style={{ marginBottom: isMobile ? '12px' : '16px' }}
                />
              )}
            </Card>

            {/* Action Buttons */}
            <Card 
              title="Acciones"
              size="small"
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleApplyDiscount}
                  loading={loading}
                  disabled={!isFormValid}
                  block
                  size={isMobile ? "middle" : "large"}
                  style={{ backgroundColor: '#52c41a' }}
                >
                  Aplicar Descuento
                </Button>

                <Button
                  icon={<CalculatorOutlined />}
                  onClick={handleReset}
                  block
                  disabled={loading}
                >
                  Resetear Campos
                </Button>

                <Divider style={{ margin: '12px 0' }} />

                <Button
                  icon={<CloseOutlined />}
                  onClick={onCancel}
                  block
                  disabled={loading}
                >
                  Cancelar
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      <ItemDiscountModal
        visible={!!itemTarget}
        order={order}
        item={itemTarget}
        onCancel={() => setItemTarget(null)}
        onApplied={() => {
          setItemTarget(null);
          onItemDiscountApplied?.();
        }}
      />
    </Modal>
  );
};

export default DiscountModal;